import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { ProductStoreService } from '../instances/product-store';
import { PostProductStoreRequest, PutProductStoreRequest } from '../request/product-store';
import { ProductStoreConverter } from '@/converters/product-store';

export const useProductStores = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_PRODUCT_STORES', params],
    async () => {
      const [err, data] = await ProductStoreService.getProductStores(params);
      if (err) {
        throw err;
      }
      return {
        data: ProductStoreConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostProductStoreMutation = () => {
  return useMutation({
    mutationKey: ['POST_MEDIA_PRODUCT'],
    mutationFn: async (payload: PostProductStoreRequest) => {
      const [err, data] = await ProductStoreService.postProductStore(payload);
      if (err) {
        throw err;
      }
      return ProductStoreConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutProductStoreMutation = () => {
  return useMutation({
    mutationKey: ['PUT_MEDIA_PRODUCT'],
    mutationFn: async ({ uuid, payload }: { uuid: string; payload: PutProductStoreRequest }) => {
      const [err, data] = await ProductStoreService.putProductStore(uuid, payload);
      if (err) {
        throw err;
      }
      return ProductStoreConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteProductStoreMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_MEDIA_PRODUCT'],
    mutationFn: async (uuid: string) => {
      const [err] = await ProductStoreService.deleteProductStore(uuid);
      if (err) {
        throw err;
      }
    },
  });
};

export const useGetProductStoreByUUID = () => {
  return useMutation({
    mutationKey: ['GET_MEDIA_PRODUCT_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await ProductStoreService.getProductStoreById(uuid);
      if (err) {
        throw err;
      }
      return ProductStoreConverter.toDomainFromByIdResponse(data);
    },
  });
};
